import { useState, useCallback, useRef, useMemo } from "react";
import { IItemService } from "../services/soap/item/ItemService";
import { GetItemTypeRequest } from "../services/soap/item/requests/GetItemTypeRequest";
import { GetCodebookRequest } from "../services/soap/item/requests/GetCodebookRequest";
import { GetItemTypeResponse } from "../services/soap/item/responses/GetItemTypeResponse";
import { GetCodebookResponse } from "../services/soap/item/responses/GetCodebookResponse";
import { AttributeData } from "../components/protected/layout-components/ProjectProduct/ProjectProductAddEdit";

interface Store {
  Server: string;
  SessionId: string;
}

/**
 * Custom hook for managing item attributes cache with multi-type support.
 * 
 * Features:
 * - Multi-type cache: Stores attributes keyed by itemTypeID
 * - Check-and-load logic: Checks cache before fetching
 * - Race condition protection: Prevents duplicate API calls for the same itemType
 * - Memoization: Optimized for performance
 * 
 * @param itemService - The item service instance for API calls
 * @param store - Store containing Server and SessionId
 * @returns Object containing cache, getAttributes, ensureAttributesForType, and loadAttributesForTypes
 */
export function useItemAttributesCache(
  itemService: IItemService,
  store: Store
) {
  const [attributesCache, setAttributesCache] = useState<
    Record<string, AttributeData[]>
  >({});

  // Track pending requests to prevent race conditions
  const pendingRequests = useRef<Map<string, Promise<AttributeData[]>>>(
    new Map()
  );

  /**
   * Loads attribute data for a specific item type from the API.
   * This is the core data fetching function.
   */
  const loadAttributesData = useCallback(
    async (item_type: string): Promise<AttributeData[]> => {
      const itemTypeRes: GetItemTypeResponse = await itemService.getItemType(
        new GetItemTypeRequest(store.Server, store.SessionId, item_type)
      );
      let codebookRes: GetCodebookResponse | null = null;

      const attrib0: AttributeData = {
        filter: itemTypeRes.ITEM_TYPE.attrib0_filter,
        name: itemTypeRes.ITEM_TYPE.attrib0_name,
        priority: itemTypeRes.ITEM_TYPE.attrib0_priority,
        type: itemTypeRes.ITEM_TYPE.attrib0_type,
        values_name: itemTypeRes.ITEM_TYPE.attrib0_values_name,
        values: itemTypeRes.ITEM_TYPE.attrib0_values,
      };

      const attrib1: AttributeData = {
        filter: itemTypeRes.ITEM_TYPE.attrib1_filter,
        name: itemTypeRes.ITEM_TYPE.attrib1_name,
        priority: itemTypeRes.ITEM_TYPE.attrib1_priority,
        type: itemTypeRes.ITEM_TYPE.attrib1_type,
        values_name: itemTypeRes.ITEM_TYPE.attrib1_values_name,
        values: itemTypeRes.ITEM_TYPE.attrib1_values,
      };

      const attrib2: AttributeData = {
        filter: itemTypeRes.ITEM_TYPE.attrib2_filter,
        name: itemTypeRes.ITEM_TYPE.attrib2_name,
        priority: itemTypeRes.ITEM_TYPE.attrib2_priority,
        type: itemTypeRes.ITEM_TYPE.attrib2_type,
        values_name: itemTypeRes.ITEM_TYPE.attrib2_values_name,
        values: itemTypeRes.ITEM_TYPE.attrib2_values,
      };

      // Load codebooks for type 5 attributes
      if (Number(attrib0.type) === 5 && attrib0.values) {
        codebookRes = await itemService.getCodebook(
          new GetCodebookRequest(store.Server, store.SessionId, attrib0.values)
        );
        attrib0.codebooks = codebookRes.CODEBOOK[0].VALUES.map((item: any) => {
          return {
            id: item.id,
            name: item.name,
            priority: item.priority,
          };
        });
      }

      if (Number(attrib1.type) === 5 && attrib1.values) {
        codebookRes = await itemService.getCodebook(
          new GetCodebookRequest(store.Server, store.SessionId, attrib1.values)
        );
        attrib1.codebooks = codebookRes.CODEBOOK[0].VALUES.map((item: any) => {
          return {
            id: item.id,
            name: item.name,
            priority: item.priority,
          };
        });
      }

      if (Number(attrib2.type) === 5 && attrib2.values) {
        codebookRes = await itemService.getCodebook(
          new GetCodebookRequest(store.Server, store.SessionId, attrib2.values)
        );
        attrib2.codebooks = codebookRes.CODEBOOK[0].VALUES.map((item: any) => {
          return {
            id: item.id,
            name: item.name,
            priority: item.priority,
          };
        });
      }

      return [attrib0, attrib1, attrib2];
    },
    [itemService, store.Server, store.SessionId]
  );

  /**
   * Ensures attributes for a specific item type are loaded.
   * Implements check-and-load logic with race condition protection.
   * 
   * @param itemTypeID - The item type ID to load attributes for
   * @returns Promise resolving to the attribute data array
   */
  const ensureAttributesForType = useCallback(
    async (itemTypeID: string): Promise<AttributeData[]> => {
      if (!itemTypeID) {
        return [];
      }

      const normalizedType = String(itemTypeID);

      // Check cache first
      if (attributesCache[normalizedType]) {
        return attributesCache[normalizedType];
      }

      // Check if request is already pending (race condition protection)
      if (pendingRequests.current.has(normalizedType)) {
        return pendingRequests.current.get(normalizedType)!;
      }

      // Create and store promise
      const promise = loadAttributesData(normalizedType);
      pendingRequests.current.set(normalizedType, promise);

      try {
        const result = await promise;
        // Update cache
        setAttributesCache((prev) => ({
          ...prev,
          [normalizedType]: result,
        }));
        return result;
      } catch (error) {
        // Remove from pending on error so it can be retried
        pendingRequests.current.delete(normalizedType);
        throw error;
      } finally {
        // Clean up pending request after completion
        pendingRequests.current.delete(normalizedType);
      }
    },
    [attributesCache, loadAttributesData]
  );

  /**
   * Gets attributes for a specific item type from cache (synchronous).
   * Returns undefined if not cached.
   * 
   * @param itemTypeID - The item type ID to get attributes for
   * @returns Attribute data array or undefined if not cached
   */
  const getAttributes = useCallback(
    (itemTypeID: string | undefined): AttributeData[] | undefined => {
      if (!itemTypeID) {
        return undefined;
      }
      const normalizedType = String(itemTypeID);
      return attributesCache[normalizedType];
    },
    [attributesCache]
  );

  /**
   * Loads attributes for multiple item types in parallel.
   * Useful for batch loading when you have multiple item types in a tree.
   * 
   * @param itemTypeIDs - Array of item type IDs to load
   * @returns Promise that resolves when all types are loaded
   */
  const loadAttributesForTypes = useCallback(
    async (itemTypeIDs: (string | undefined)[]): Promise<void> => {
      const uniqueTypes = [
        ...new Set(itemTypeIDs.filter(Boolean).map(String)),
      ];
      await Promise.all(uniqueTypes.map((type) => ensureAttributesForType(type)));
    },
    [ensureAttributesForType]
  );

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      attributesCache,
      getAttributes,
      ensureAttributesForType,
      loadAttributesForTypes,
    }),
    [attributesCache, getAttributes, ensureAttributesForType, loadAttributesForTypes]
  );
}
